// Overview metrics
export const overviewMetrics = [
  {
    title: 'Total Submissions',
    value: '1,247',
    change: '+12% from last consultation',
    trend: 'up',
    color: 'bg-blue-100',
  },
  {
    title: 'Processing Complete',
    value: '94%',
    change: '1,172 of 1,247',
    trend: 'neutral',
    color: 'bg-blue-100',
  },
  {
    title: 'Average Processing Time',
    value: '2.3 min',
    change: '-85% from manual process',
    trend: 'down',
    color: 'bg-blue-100',
  },
  {
    title: 'Items Flagged for Review',
    value: 78,
    change: '6.3% of submissions',
    trend: 'neutral',
    color: 'bg-blue-100',
  },
]
// Thematic analysis data
export const themes = [
  {
    title: 'Water Quality Standards',
    count: 823,
    description:
      'Concerns about proposed nitrate levels and monitoring requirements for freshwater bodies.',
    priority: 'critical',
    confidence: 92,
    treatyRelevant: true,
  },
  {
    title: 'Agricultural Runoff',
    count: 756,
    description:
      'Submissions related to farm practices, fencing requirements, and riparian planting.',
    priority: 'high',
    confidence: 88,
    treatyRelevant: false,
  },
  {
    title: 'Urban Water Infrastructure',
    count: 612,
    description:
      'Focus on stormwater management, sewage treatment, and municipal water systems.',
    priority: 'medium',
    confidence: 85,
    treatyRelevant: false,
  },
  {
    title: 'Māori Rights and Interests',
    count: 489,
    description:
      'Submissions addressing Iwi/Hapū water rights, co-governance, and cultural values.',
    priority: 'high',
    confidence: 79,
    treatyRelevant: true,
  },
  {
    title: 'Enforcement Mechanisms',
    count: 412,
    description:
      'Comments on penalties, monitoring regimes, and compliance frameworks.',
    priority: 'medium',
    confidence: 82,
    treatyRelevant: false,
  },
  {
    title: 'Economic Impact',
    count: 387,
    description:
      'Analysis of costs to businesses, ratepayers, and implementation timeframes.',
    priority: 'high',
    confidence: 77,
    treatyRelevant: false,
  },
]
// Stakeholder data
export const stakeholders = [
  {
    type: 'Individual Citizens',
    count: 742,
    percentage: 59,
    color: 'bg-blue-500',
  },
  {
    type: 'Industry/Business',
    count: 217,
    percentage: 17,
    color: 'bg-blue-400',
  },
  {
    type: 'NGOs/Environmental Groups',
    count: 156,
    percentage: 13,
    color: 'bg-blue-300',
  },
  {
    type: 'Iwi/Hapū',
    count: 87,
    percentage: 7,
    color: 'bg-blue-200',
  },
  {
    type: 'Local Government',
    count: 45,
    percentage: 4,
    color: 'bg-blue-100',
  },
]
// Evidence review data
export const evidenceItems = [
  {
    title: 'NIWA Freshwater Quality Report 2023',
    source: 'National Institute of Water and Atmospheric Research',
    submissionCount: 187,
    validationStatus: 'validated',
    priority: 'high',
    confidence: 95,
  },
  {
    title: 'Economic Impact of Water Reforms on Agricultural Sector',
    source: 'Industry Association Research',
    submissionCount: 142,
    validationStatus: 'conflicting',
    priority: 'high',
    confidence: 68,
  },
  {
    title: 'Cultural Health Index Assessments for Major Waterways',
    source: 'Iwi Environmental Authorities Collective',
    submissionCount: 98,
    validationStatus: 'validated',
    priority: 'high',
    confidence: 87,
  },
  {
    title: 'Municipal Water Treatment Costs Analysis',
    source: 'Local Government Association',
    submissionCount: 76,
    validationStatus: 'unverified',
    priority: 'medium',
    confidence: 72,
  },
]
// Position mapping data
export const positions = [
  {
    position: 'Support with stronger enforcement',
    count: 412,
    percentage: 33,
    stakeholders: ['Environmental NGOs', 'Community Groups', 'Urban Residents'],
    compromiseOpportunity: true,
    confidence: 89,
  },
  {
    position: 'Support with longer implementation timeline',
    count: 325,
    percentage: 26,
    stakeholders: ['Small Businesses', 'Local Councils', 'Some Iwi Groups'],
    compromiseOpportunity: true,
    confidence: 84,
  },
  {
    position: 'Oppose due to economic costs',
    count: 287,
    percentage: 23,
    stakeholders: ['Agricultural Sector', 'Manufacturing', 'Rural Communities'],
    compromiseOpportunity: false,
    confidence: 82,
  },
  {
    position: 'Seeking co-governance guarantees',
    count: 143,
    percentage: 11,
    stakeholders: ['Iwi Authorities', 'Māori Land Trusts'],
    compromiseOpportunity: true,
    confidence: 76,
  },
  {
    position: 'Oppose due to insufficient protections',
    count: 80,
    percentage: 7,
    stakeholders: ['Conservation Trusts', 'Scientific Community'],
    compromiseOpportunity: false,
    confidence: 81,
  },
]
// Individual submissions data
export const submissions = [
  {
    id: 'SUB-2023-0472',
    submitter: 'Waikato Rivers Association',
    stakeholderType: 'NGO',
    date: '15 Apr 2023',
    excerpt:
      'Our monitoring data indicates that the proposed nitrate thresholds are insufficient to protect freshwater ecosystems in lowland streams. We recommend adopting the more stringent standard of 1.0 mg/L as recommended by the Science Advisory Group.',
    themes: ['Water Quality Standards', 'Enforcement Mechanisms'],
    priority: 'high',
    confidence: 91,
    treatyRelevant: false,
  },
  {
    id: 'SUB-2023-0183',
    submitter: 'Te Arawa Lakes Trust',
    stakeholderType: 'Iwi Authority',
    date: '12 Apr 2023',
    excerpt:
      'The proposed framework fails to adequately recognize and provide for the exercise of kaitiakitanga by mana whenua. We seek specific provisions that enable co-governance of waterways within our rohe and the incorporation of mātauranga Māori in monitoring regimes.',
    themes: ['Māori Rights and Interests', 'Water Quality Standards'],
    priority: 'high',
    confidence: 87,
    treatyRelevant: true,
  },
  {
    id: 'SUB-2023-0629',
    submitter: 'Federated Farmers Canterbury',
    stakeholderType: 'Industry Association',
    date: '18 Apr 2023',
    excerpt:
      'The economic analysis underestimates the compliance costs for farmers, particularly in Canterbury where significant investments in irrigation infrastructure have already been made. We propose a phased implementation approach with targeted support for affected farmers.',
    themes: ['Economic Impact', 'Agricultural Runoff'],
    priority: 'medium',
    confidence: 84,
    treatyRelevant: false,
  },
  {
    id: 'SUB-2023-0891',
    submitter: 'Wellington City Council',
    stakeholderType: 'Local Government',
    date: '20 Apr 2023',
    excerpt:
      'The capital expenditure required to upgrade stormwater systems to meet the new standards exceeds our current Long Term Plan allocations. We support the direction of the reforms but request a longer implementation timeframe to align with infrastructure renewal cycles.',
    themes: ['Urban Water Infrastructure', 'Economic Impact'],
    priority: 'medium',
    confidence: 89,
    treatyRelevant: false,
  },
]
